import moment from 'moment';
import _ from 'lodash';
import template from './am-bulk-upload-operators.html';

const ValidationStatusEnum = Object.freeze({
  validating: "validating",
  error: "error",
  warning: "warning",
  ok: null
});

const OperatorCsvValidationErrorEnum = Object.freeze({
  noName: "no-name",
  invalidCodeFormat: "invalid-code-format",
});

class AmBulkUploadOperatorsController {
  constructor(FileUploader, amOperatorsService) {
    'ngInject';
    this.service = amOperatorsService;
    this.FileUploader = FileUploader;
    this.uploader = new FileUploader();
    this.selectedFile = null;
    this.someOperatorsHasNoCodes = false;
    this.validationStatus = ValidationStatusEnum.ok;
    this.validationError = null;
    this.validationErrorMessages = [];
    this.creating = false;
    // will hold a list of operators ready to be created
    this.operatorsData = [];
    // holds successful response errors for user review
    this.errors = [];
    this.feed = {};
  }

  $onInit() {
    this.resetFileUploader();
    this.updateCSVFileNames();
  }
  $onChanges() { }
  $onDestroy() {
    this.uploader.destroy();
  }
  resetFileUploader() {
    this.uploader.clearQueue();
    this.uploader.queueLimit = 1;
    this.uploader.onAfterAddingAll = this.afterFilesSelected.bind(this);
  }
  afterFilesSelected() {
    this.fileValidationStart();
    let lastItem = this.uploader.queue.pop();
    this.service
      .operatorsCsvToJson(lastItem._file)
      .then(response => {
        //set the selected file and any warning message
        this.setProperties(lastItem._file, response.data);
      })
      .catch(error => {
        this.setProperties(null, null, error);
      });
  }
  fileValidationStart() {
    this.validationStatus = ValidationStatusEnum.validating;
    this.selectedFile = null;
    this.operatorsData = [];
    this.validationError = null;
    this.validationErrorMessages = [];
  }
  setProperties(file, successResponseData, errorResponse) {
    this.selectedFile = file;
    const { operators: responseOperators = [] } = successResponseData || {};
    this.operatorsData = responseOperators;

    if (errorResponse) {
      this.validationStatus = ValidationStatusEnum.error;
      this.validationError = errorResponse.data;
    } else {
      const someOperatorsHasNoCodes = this.operatorsData.some(
        op => _.get(op, 'AccessCodes', []).length === 0 && !op.AutoGeneratePIN
      );

      const { errors = [] } = successResponseData || {};

      this.errors = errors;
      const successHasErrors = this.errors.length > 0;

      if (successHasErrors) {
        this.validationStatus = ValidationStatusEnum.error;
        this.setValidationErrorMessages();
      } else {
        this.validationStatus = someOperatorsHasNoCodes ? ValidationStatusEnum.warning : null;
      }
    }
  }
  openFileLoader() {
    this.feed = {};
    this.resetFileUploader();
    const fileElem = document.getElementById('file-input');
    //reset value of file input for uploader to allow reselecting the same file
    fileElem.value = '';
  }
  createOperators() {
    if (this.operatorsData && this.operatorsData.length < 1) return;
    this.feed = {};
    this.creating = true;
    this.feed.total = this.operatorsData.length;
    this.service
      .bulkCreateOperators(this.operatorsData)
      .then(response => {
        //set feed back to the user
        this.updateFeed(response.data);
        //reset processed operators data and validationStatus but keep file name
        this.setProperties(this.selectedFile);
      })
      .catch(() => {
        this.feed.error = true;
        this.feed.failedOperators = this.operatorsData.slice();
        this.feed.createdOperators = [];
      })
      .finally(() => {
        this.creating = false;
        this.updateCSVFileNames();
      });
  }

  updateCSVFileNames() {
    const datetimeStamp = moment()
      .utc()
      .format('YY-MM-DDTHH:mm:ss');
    this.createdOperatorsFileName = `created-operators-${datetimeStamp}.csv`;
    this.failedOperatorsFileName = `operators-errors-${datetimeStamp}.csv`;
  }

  updateFeed(csvFeedbackResult = []) {
    if (!this.feed) {
      this.feed = {
        failedOperators: [],
        createdOperators: []
      };
    }

    const failedOperators = csvFeedbackResult.filter(opfeed => !opfeed.isCreated);
    const createdOperators = csvFeedbackResult.filter(opfeed => opfeed.isCreated);

    this.feed.failedOperators = [...failedOperators];
    this.feed.createdOperators = [...createdOperators];
  }

  getCreatedOperatorsCsvData() {
    return this.service.generateCreatedOperatorsCsvData(this.feed.createdOperators);
  }

  getInvalidCsvData() {
    const errorData = this.errors.map(error => ({
      row: error.row,
      name: error.row.Name,
      rowNumber: error.rowNumber,
      errorCode: error.code
    }));

    return this.service.generateFailedCreateOperatorsCsvData(errorData);
  }

  getFailedCsvData() {
    return this.service.generateFailedCreateOperatorsCsvData(this.feed.failedOperators);
  }

  getValidationErrorMessage(err = {}) {
    const rowNumber = err.rowNumber || "";
    const errCode = err.code || undefined;

    switch (errCode) {
      case OperatorCsvValidationErrorEnum.noName:
        return `The operator at row ${rowNumber} is missing a name.`;
      case OperatorCsvValidationErrorEnum.invalidCodeFormat:
        return `The operator at row ${rowNumber} has an invalid code.`;
      default:
        return "Something went wrong with one or more of the uploaded operators.";
    }
  }

  setValidationErrorMessages() {
    this.resetValidationErrorMessages();
    this.validationErrorMessages = this.errors.map(err => this.getValidationErrorMessage(err));
  }

  resetValidationErrorMessages() {
    this.validationErrorMessages = [];
  }
}

export default {
  template,
  controller: AmBulkUploadOperatorsController,
  controllerAs: 'bulkOpVm',
  bindings: {}
};
